import React, { useState, useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PaymentModal = ({ cart, selectedMember, onClose, onComplete }) => {
  const [paymentMethod, setPaymentMethod] = useState('credit_card');
  const [discount, setDiscount] = useState(0);
  const [discountReason, setDiscountReason] = useState('');
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [processing, setProcessing] = useState(false);
  const [cashTendered, setCashTendered] = useState('');

  const paymentMethods = [
    { id: 'cash', label: 'Cash', icon: 'Banknote' },
    { id: 'credit_card', label: 'Credit Card', icon: 'CreditCard' },
    { id: 'debit_card', label: 'Debit Card', icon: 'CreditCard' },
    { id: 'digital_wallet', label: 'Digital Wallet', icon: 'Smartphone' },
    { id: 'member_account', label: 'Member Account', icon: 'User' }
  ];

  const totals = useMemo(() => {
    const subtotal = cart?.reduce((sum, item) => {
      const price = parseFloat(item?.product?.base_price);
      return sum + (price * item?.quantity);
    }, 0);

    const tax = cart?.reduce((sum, item) => {
      const price = parseFloat(item?.product?.base_price);
      const taxRate = parseFloat(item?.product?.tax_rate || 0) / 100;
      return sum + (price * item?.quantity * taxRate);
    }, 0);

    const discountAmount = parseFloat(discount || 0);
    const total = subtotal + tax - discountAmount;
    const tenderedAmount = parseFloat(cashTendered || 0);
    const change = paymentMethod === 'cash' ? Math.max(0, tenderedAmount - total) : 0;

    return { 
      subtotal, 
      tax, 
      discount: discountAmount, 
      total: Math?.max(0, total),
      cashTendered: tenderedAmount,
      change
    };
  }, [cart, discount, cashTendered, paymentMethod]);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    // Validate cash payment
    if (paymentMethod === 'cash') {
      if (!cashTendered || parseFloat(cashTendered) < totals?.total) {
        alert('Cash tendered must be greater than or equal to total amount');
        return;
      }
    }
    
    setProcessing(true);

    try {
      await onComplete?.({
        paymentMethod,
        discount: totals?.discount,
        discountReason: discountReason?.trim() || null,
        reference: reference?.trim() || null,
        notes: notes?.trim() || null,
        cashTendered: paymentMethod === 'cash' ? totals?.cashTendered : null,
        changeGiven: paymentMethod === 'cash' ? totals?.change : null
      });
    } catch (error) {
      console.error('Payment error:', error);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border sticky top-0 bg-card">
          <h2 className="text-xl font-bold text-foreground">Complete Payment</h2>
          <button
            onClick={onClose}
            disabled={processing}
            className="text-muted-foreground hover:text-foreground transition-smooth"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {/* Order Summary */}
          <div className="mb-6 p-4 bg-background rounded-lg border border-border">
            <h3 className="text-sm font-semibold text-foreground mb-3">Order Summary</h3>
            <div className="space-y-2 text-sm">
              {cart?.map((item) => (
                <div key={item?.product?.id} className="flex items-center justify-between">
                  <span className="text-muted-foreground">
                    {item?.product?.name} x {item?.quantity}
                  </span>
                  <span className="text-foreground font-medium">
                    ${(item?.product?.base_price * item?.quantity)?.toFixed(2)}
                  </span>
                </div>
              ))}
              <div className="flex items-center justify-between pt-2 border-t border-border">
                <span className="text-muted-foreground">Subtotal</span>
                <span className="text-foreground font-medium">${totals?.subtotal?.toFixed(2)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Tax</span>
                <span className="text-foreground font-medium">${totals?.tax?.toFixed(2)}</span>
              </div>
              {totals?.discount > 0 && (
                <div className="flex items-center justify-between text-success">
                  <span>Discount</span>
                  <span className="font-medium">-${totals?.discount?.toFixed(2)}</span>
                </div>
              )}
              <div className="flex items-center justify-between text-lg font-bold pt-2 border-t border-border">
                <span className="text-foreground">Total</span>
                <span className="text-primary">${totals?.total?.toFixed(2)}</span>
              </div>
              
              {/* Cash Payment Info */}
              {paymentMethod === 'cash' && cashTendered && (
                <>
                  <div className="flex items-center justify-between text-success">
                    <span>Cash Tendered</span>
                    <span className="font-medium">${totals?.cashTendered?.toFixed(2)}</span>
                  </div>
                  {totals?.change > 0 && (
                    <div className="flex items-center justify-between text-lg font-bold text-warning">
                      <span>Change Due</span>
                      <span>${totals?.change?.toFixed(2)}</span>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Payment Method */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-foreground mb-3">
              Payment Method *
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {paymentMethods?.map((method) => (
                <button
                  key={method?.id}
                  type="button"
                  onClick={() => setPaymentMethod(method?.id)}
                  className={`flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-smooth ${
                    paymentMethod === method?.id
                      ? 'border-primary bg-primary/10' :'border-border hover:border-primary/50'
                  }`}
                >
                  <Icon
                    name={method?.icon}
                    size={24}
                    className={paymentMethod === method?.id ? 'text-primary' : 'text-muted-foreground'}
                  />
                  <span className={`text-sm font-medium ${
                    paymentMethod === method?.id ? 'text-primary' : 'text-muted-foreground'
                  }`}>
                    {method?.label}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Cash Tendered (only for cash payments) */}
          {paymentMethod === 'cash' && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-foreground mb-2">
                Cash Tendered *
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                <input
                  type="number"
                  step="0.01"
                  min={totals?.total}
                  value={cashTendered}
                  onChange={(e) => setCashTendered(e?.target?.value)}
                  placeholder="0.00"
                  className="w-full pl-8 pr-4 py-2 rounded-lg bg-background border border-border focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
                  required
                />
              </div>
              {cashTendered && totals?.change > 0 && (
                <p className="mt-2 text-sm text-warning font-medium">
                  Change to return: ${totals?.change?.toFixed(2)}
                </p>
              )}
            </div>
          )}

          {/* Discount */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-foreground mb-2">
              Discount Amount
            </label>
            <div className="grid grid-cols-2 gap-3">
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max={totals?.subtotal + totals?.tax}
                  value={discount}
                  onChange={(e) => setDiscount(e?.target?.value)}
                  className="w-full pl-8 pr-4 py-2 rounded-lg bg-background border border-border focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
                />
              </div>
              <input
                type="text"
                value={discountReason}
                onChange={(e) => setDiscountReason(e?.target?.value)}
                placeholder="Reason (optional)"
                className="w-full px-4 py-2 rounded-lg bg-background border border-border focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
              />
            </div>
          </div>

          {/* Reference */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-foreground mb-2">
              Payment Reference
            </label>
            <input
              type="text"
              value={reference}
              onChange={(e) => setReference(e?.target?.value)}
              placeholder="Transaction ID, check number, etc."
              className="w-full px-4 py-2 rounded-lg bg-background border border-border focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
            />
          </div>

          {/* Notes */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-foreground mb-2">
              Notes
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e?.target?.value)}
              placeholder="Additional notes..."
              rows={3}
              className="w-full px-4 py-2 rounded-lg bg-background border border-border focus:outline-none focus:ring-2 focus:ring-primary text-foreground resize-none"
            />
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3">
            <Button
              type="button"
              variant="ghost"
              onClick={onClose}
              disabled={processing}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={processing}
              className="flex-1"
            >
              {processing ? (
                <>
                  <Icon name="Loader2" size={18} className="mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Icon name="CheckCircle" size={18} className="mr-2" />
                  Complete Payment
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PaymentModal;